import { useEffect, useState } from "react";
import { useTheme } from "../contexts/ThemeContext";
import { useAuth } from "../contexts/AuthContext";
import {
  listRoteiros,
  downloadRoteiroPdf,
  fetchRoteiroPdfBlob,
  cancelarRoteiro,
  reativarRoteiro,
  duplicarRoteiro,
  duplicarComoGrupo,
} from "../services/roteirosService";
import { formatMoeda } from "../utils/moeda";
import StatusBadge from "../components/ui/StatusBadge";
import Loading, { ErrorBox } from "../components/ui/Loading";
import RoteiroFormModal from "../components/forms/RoteiroFormModal";
import RoteiroDiaFormModal from "../components/forms/RoteiroDiaFormModal";
import PdfOptionsModal from "../components/forms/PdfOptionsModal";
import RoteiroTarefasSection from "../components/tarefas/RoteiroTarefasSection";
import { DAYS_PT_LONG } from "../themes/themes";

function formatDate(iso) {
  if (!iso) return "—";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}`;
}

function dias(inicio, fim) {
  if (!inicio || !fim) return 0;
  const a = new Date(inicio);
  const b = new Date(fim);
  return Math.floor((b - a) / (1000 * 60 * 60 * 24)) + 1;
}

// Calcula a data (Date local) do dia N de um roteiro a partir de DataInicio.
// Parse manual do ISO pra evitar deslocamento de timezone — `new Date("2026-10-03")`
// é interpretado como UTC midnight, o que pode deslocar o dia em fusos ao oeste.
function diaDataForRoteiro(dataInicio, numeroDia) {
  if (!dataInicio) return null;
  const [y, m, d] = dataInicio.split("-").map(Number);
  const base = new Date(y, m - 1, d);
  base.setDate(base.getDate() + (numeroDia - 1));
  return base;
}

function fmtDateDMY(date) {
  if (!date) return "";
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  return `${dd}/${mm}/${date.getFullYear()}`;
}

function isDiaEmpty(d) {
  return (
    !d.titulo &&
    !d.atividades &&
    !d.transporte &&
    !d.hospedagem &&
    !d.refeicoes &&
    !d.notas
  );
}

export default function RoteirosPage() {
  const { C } = useTheme();
  const { profile } = useAuth();
  // PR3 moeda-por-guia: símbolo único da moeda do guia (post-PR2, todo Roteiro
  // do guia herda essa moeda). r.moeda no DTO é vestígio.
  const moeda = profile?.moeda ?? "EUR";
  const [roteiros, setRoteiros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [mostrarCancelados, setMostrarCancelados] = useState(false);

  // Estado do modal de RoteiroDia
  const [diaModalOpen, setDiaModalOpen] = useState(false);
  const [diaEditing, setDiaEditing] = useState(null); // { roteiroId, dia | null }

  // Estados independentes — Visualizar e Baixar PDF não compartilham lock.
  // Usuária pode acionar um botão enquanto o outro está processando.
  const [previewLoading, setPreviewLoading] = useState(null);
  const [downloadLoading, setDownloadLoading] = useState(null);

  // Modal de opções do PDF (passageiros / dias / valores). Abre antes de gerar;
  // Luciana confirma/ajusta e aí a geração começa. `acao` guarda se é preview
  // ou download pra saber qual fluxo executar após o confirm.
  const [pdfModal, setPdfModal] = useState({ open: false, roteiro: null, acao: null });

  function handleVisualizarPdf(r, e) {
    e?.stopPropagation();
    if (previewLoading === r.id) return;
    setPdfModal({ open: true, roteiro: r, acao: "preview" });
  }

  function handleBaixarPdf(r, e) {
    e?.stopPropagation();
    if (downloadLoading === r.id) return;
    setPdfModal({ open: true, roteiro: r, acao: "download" });
  }

  function handleConfirmPdf(options) {
    // Captura ANTES do setState (react batches; queremos os valores desta call).
    const acao = pdfModal.acao;
    const r = pdfModal.roteiro;
    setPdfModal({ open: false, roteiro: null, acao: null });
    if (!r) return;
    if (acao === "preview") doPreviewPdf(r, options);
    else doDownloadPdf(r, options);
  }

  async function doPreviewPdf(r, options) {
    // CRÍTICO: abre a aba ANTES do await pra preservar o user gesture.
    // Executa dentro do mesmo click handler que confirma o modal (sincrono até
    // window.open), então o browser aceita como popup solicitado.
    const newTab = window.open("", "_blank");
    if (!newTab) {
      alert("Permita pop-ups pra visualizar o PDF.");
      return;
    }
    setPreviewLoading(r.id);
    try {
      const blob = await fetchRoteiroPdfBlob(r.id, options);
      const url = window.URL.createObjectURL(blob);
      newTab.location.href = url;
      // Revoga após 60s — tempo suficiente pro browser renderizar o PDF.
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      newTab.close();
      alert(err?.response?.data?.message || "Não foi possível gerar o PDF.");
    } finally {
      setPreviewLoading(null);
    }
  }

  async function doDownloadPdf(r, options) {
    setDownloadLoading(r.id);
    try {
      const slug = (r.titulo || "roteiro")
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, 60);
      await downloadRoteiroPdf(r.id, `roteiro-${r.id}-${slug}.pdf`, options);
    } catch (err) {
      alert(err?.response?.data?.message || "Não foi possível gerar o PDF.");
    } finally {
      setDownloadLoading(null);
    }
  }

  async function reload() {
    setLoading(true);
    try {
      const res = await listRoteiros({ incluirCancelados: mostrarCancelados });
      setRoteiros(res);
      setError(null);
    } catch {
      setError("Não foi possível carregar os roteiros.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mostrarCancelados]);

  async function handleCancelar(r, e) {
    e?.stopPropagation();
    if (!window.confirm(`Cancelar o roteiro "${r.titulo}"?\n\nParcelas não pagas serão removidas; parcelas já pagas ficam como histórico. A agenda será liberada.`))
      return;
    try {
      await cancelarRoteiro(r.id);
      await reload();
    } catch (err) {
      alert(err?.response?.data?.message || "Não foi possível cancelar o roteiro.");
    }
  }

  async function handleReativar(r, e) {
    e?.stopPropagation();
    if (!window.confirm(`Reativar "${r.titulo}"?\n\nO roteiro volta para Rascunho. Você precisa abrir e mudar para Confirmado de novo para reservar a agenda e gerar parcelas.`))
      return;
    try {
      await reativarRoteiro(r.id);
      await reload();
    } catch (err) {
      alert(err?.response?.data?.message || "Não foi possível reativar o roteiro.");
    }
  }

  // Duplica um roteiro como template. Backend copia dias e tarefas resetadas;
  // recarrega a lista e abre o modal do novo pra Luciana ajustar cliente/datas.
  async function handleDuplicar(r, e) {
    e?.stopPropagation();
    try {
      const novo = await duplicarRoteiro(r.id);
      await reload();
      setEditing(novo);
      setModalOpen(true);
    } catch (err) {
      alert(err?.response?.data?.message || "Não foi possível duplicar o roteiro.");
    }
  }

  // Duplica como IRMÃO do original — mesma viagem física (mesmas datas), novo
  // contrato (novo cliente, novo financeiro). Backend garante que ambos ficam
  // com o mesmo GrupoId (gera novo Guid se original era standalone).
  async function handleDuplicarGrupo(r, e) {
    e?.stopPropagation();
    if (!window.confirm(
      `Duplicar "${r.titulo}" como grupo?\n\n` +
      `Um novo roteiro é criado com as MESMAS datas (${r.dataInicio} → ${r.dataFim}) ` +
      `mas SEM cliente/financeiro. Use pra 2ª família na mesma viagem física.`
    )) return;
    try {
      const novo = await duplicarComoGrupo(r.id);
      await reload();
      setEditing(novo);
      setModalOpen(true);
    } catch (err) {
      alert(err?.response?.data?.message || "Não foi possível duplicar como grupo.");
    }
  }

  function openCreate() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(r, e) {
    e?.stopPropagation();
    setEditing(r);
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
    setEditing(null);
  }

  function toggleExpand(id) {
    setExpanded((cur) => (cur === id ? null : id));
  }

  function openEditDia(roteiroId, dia, e) {
    e?.stopPropagation();
    setDiaEditing({ roteiroId, dia });
    setDiaModalOpen(true);
  }

  function closeDiaModal() {
    setDiaModalOpen(false);
    setDiaEditing(null);
  }

  return (
    <div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        }}
      >
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>
            Roteiros
          </h1>
          <p style={{ color: C.textMuted, fontSize: 13, margin: "4px 0 0" }}>
            Montagem e gestão de itinerários
          </p>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              fontSize: 12,
              color: C.textMuted,
              cursor: "pointer",
              userSelect: "none",
            }}
          >
            <input
              type="checkbox"
              checked={mostrarCancelados}
              onChange={(e) => setMostrarCancelados(e.target.checked)}
              style={{ cursor: "pointer" }}
            />
            Mostrar cancelados
          </label>
          <button
            onClick={openCreate}
            style={{
              padding: "10px 20px",
              borderRadius: 10,
              border: "none",
              background: C.accent,
              color: "#fff",
              fontSize: 13,
              fontWeight: 700,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            + Novo Roteiro
          </button>
        </div>
      </div>

      <RoteiroFormModal
        open={modalOpen}
        roteiro={editing}
        onClose={closeModal}
        onSaved={reload}
        onDeleted={reload}
      />

      <RoteiroDiaFormModal
        open={diaModalOpen}
        roteiroId={diaEditing?.roteiroId}
        dia={diaEditing?.dia}
        proximoNumero={
          diaEditing?.dia
            ? diaEditing.dia.numeroDia
            : (roteiros.find((r) => r.id === diaEditing?.roteiroId)?.dias?.length ?? 0) + 1
        }
        maxDias={dias(
          roteiros.find((r) => r.id === diaEditing?.roteiroId)?.dataInicio,
          roteiros.find((r) => r.id === diaEditing?.roteiroId)?.dataFim
        )}
        onClose={closeDiaModal}
        onSaved={reload}
        onDeleted={reload}
      />

      <PdfOptionsModal
        open={pdfModal.open}
        roteiro={pdfModal.roteiro}
        actionLabel={pdfModal.acao === "download" ? "Baixar PDF" : "Visualizar"}
        onClose={() => setPdfModal({ open: false, roteiro: null, acao: null })}
        onConfirm={handleConfirmPdf}
      />

      {loading && <Loading />}
      {error && <ErrorBox message={error} />}

      {!loading && !error && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {roteiros.map((r) => {
            const isOpen = expanded === r.id;
            const isCancelado = r.status === 3;
            return (
              <div
                key={r.id}
                style={{
                  background: isOpen ? C.bgCardHover : C.bgCard,
                  borderRadius: 12,
                  border: isOpen
                    ? `1px solid ${C.accent}40`
                    : `1px solid ${C.border}`,
                  transition: "all 0.15s",
                  overflow: "hidden",
                  opacity: isCancelado ? 0.6 : 1,
                }}
              >
                {/* Header clicável */}
                <div
                  onClick={() => toggleExpand(r.id)}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "16px 20px",
                    cursor: "pointer",
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                    <div
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        background: C.accentDim,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontSize: 18,
                      }}
                    >
                      🗺️
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: C.text }}>
                        {r.titulo}
                      </div>
                      <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                        {r.clienteNome ?? "(sem cliente)"} · {formatDate(r.dataInicio)} →{" "}
                        {formatDate(r.dataFim)} · {dias(r.dataInicio, r.dataFim)} dias
                        {r.valorFinal != null && r.valorFinal > 0 && (
                          <>
                            {" · "}
                            <span style={{ color: C.accent, fontWeight: 600 }}>
                              {formatMoeda(r.valorFinal, moeda)}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    <StatusBadge status={r.status} kind="roteiro" />
                    {r.grupoId && (
                      <span
                        title="Este roteiro faz parte de um grupo (mesma viagem, contratos separados)"
                        style={{
                          padding: "4px 8px",
                          borderRadius: 6,
                          background: C.bgCardHover,
                          border: `1px solid ${C.accent}`,
                          color: C.accent,
                          fontSize: 10,
                          fontWeight: 700,
                        }}
                      >
                        👥 grupo
                      </span>
                    )}
                    {isCancelado ? (
                      <button
                        onClick={(e) => handleReativar(r, e)}
                        style={{
                          padding: "7px 14px",
                          borderRadius: 8,
                          border: `1px solid ${C.border}`,
                          background: "transparent",
                          color: C.textMuted,
                          fontSize: 11,
                          fontWeight: 600,
                          cursor: "pointer",
                          fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        ↻ Reativar
                      </button>
                    ) : (
                      <>
                        <button
                          onClick={(e) => openEdit(r, e)}
                          style={{
                            padding: "7px 14px",
                            borderRadius: 8,
                            border: `1px solid ${C.border}`,
                            background: "transparent",
                            color: C.textMuted,
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: "pointer",
                            fontFamily: "'DM Sans', sans-serif",
                          }}
                        >
                          ✏️ Editar
                        </button>
                        <button
                          onClick={(e) => handleDuplicar(r, e)}
                          style={{
                            padding: "7px 14px",
                            borderRadius: 8,
                            border: `1px solid ${C.border}`,
                            background: "transparent",
                            color: C.textMuted,
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: "pointer",
                            fontFamily: "'DM Sans', sans-serif",
                          }}
                          title="Duplicar roteiro como template (novo Rascunho sem cliente)"
                        >
                          📋 Duplicar
                        </button>
                        <button
                          onClick={(e) => handleDuplicarGrupo(r, e)}
                          style={{
                            padding: "7px 14px",
                            borderRadius: 8,
                            border: `1px solid ${C.border}`,
                            background: "transparent",
                            color: C.textMuted,
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: "pointer",
                            fontFamily: "'DM Sans', sans-serif",
                          }}
                          title="Duplicar como grupo — mesma viagem física, contrato separado (2 famílias juntas)"
                        >
                          👥 Duplicar em grupo
                        </button>
                        <button
                          onClick={(e) => handleCancelar(r, e)}
                          style={{
                            padding: "7px 14px",
                            borderRadius: 8,
                            border: `1px solid ${C.border}`,
                            background: "transparent",
                            color: C.textMuted,
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: "pointer",
                            fontFamily: "'DM Sans', sans-serif",
                          }}
                          title="Cancelar roteiro (mantém histórico)"
                        >
                          ✕ Cancelar
                        </button>
                      </>
                    )}
                    <span
                      style={{
                        fontSize: 14,
                        color: C.textMuted,
                        transition: "transform 0.2s",
                        transform: isOpen ? "rotate(180deg)" : "rotate(0)",
                      }}
                    >
                      ▼
                    </span>
                  </div>
                </div>

                {/* Corpo expandido — dias do roteiro */}
                {isOpen && (() => {
                  const maxDias = dias(r.dataInicio, r.dataFim);
                  const filledCount = (r.dias ?? []).filter((d) => !isDiaEmpty(d)).length;
                  const allFilled = maxDias > 0 && filledCount === maxDias;
                  return (
                  <div
                    style={{
                      padding: "0 20px 20px",
                      borderTop: `1px solid ${C.border}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginTop: 16,
                        marginBottom: 12,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
                        <h4
                          style={{
                            margin: 0,
                            fontSize: 13,
                            fontWeight: 600,
                            color: C.text,
                          }}
                        >
                          Dia a dia
                        </h4>
                        <span
                          style={{
                            fontSize: 11,
                            fontWeight: 600,
                            color: allFilled ? C.green : C.textMuted,
                          }}
                        >
                          {filledCount} de {maxDias} dias preenchidos
                          {allFilled && " ✓"}
                        </span>
                      </div>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button
                          onClick={(e) => handleVisualizarPdf(r, e)}
                          disabled={previewLoading === r.id}
                          style={{
                            padding: "6px 12px",
                            borderRadius: 8,
                            border: "none",
                            background: C.accent,
                            color: "#fff",
                            fontSize: 11,
                            fontWeight: 700,
                            cursor: previewLoading === r.id ? "wait" : "pointer",
                            fontFamily: "'DM Sans', sans-serif",
                            opacity: previewLoading === r.id ? 0.6 : 1,
                          }}
                          title="Abrir PDF em nova aba"
                        >
                          {previewLoading === r.id ? "Carregando..." : "👁️ Visualizar"}
                        </button>
                        <button
                          onClick={(e) => handleBaixarPdf(r, e)}
                          disabled={downloadLoading === r.id}
                          style={{
                            padding: "6px 12px",
                            borderRadius: 8,
                            border: `1px solid ${C.accent}`,
                            background: "transparent",
                            color: C.accent,
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: downloadLoading === r.id ? "wait" : "pointer",
                            fontFamily: "'DM Sans', sans-serif",
                            opacity: downloadLoading === r.id ? 0.6 : 1,
                          }}
                          title="Baixar PDF do roteiro"
                        >
                          {downloadLoading === r.id ? "Baixando..." : "⬇ Baixar PDF"}
                        </button>
                      </div>
                    </div>

                    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} onClick={(e) => e.stopPropagation()}>
                      {(r.dias ?? []).map((d) => {
                        const diaData = diaDataForRoteiro(r.dataInicio, d.numeroDia);
                        const diaSemana = diaData ? DAYS_PT_LONG[diaData.getDay()] : "";
                        const empty = isDiaEmpty(d);
                        return (
                          <div
                            key={d.id}
                            onClick={(e) => openEditDia(r.id, d, e)}
                            style={{
                              padding: "12px 14px",
                              background: C.bg,
                              borderRadius: 10,
                              borderLeft: `3px solid ${C.accent}`,
                              cursor: "pointer",
                              transition: "all 0.15s",
                            }}
                          >
                            <div
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                marginBottom: 6,
                                gap: 10,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  flexDirection: "column",
                                  gap: 2,
                                  minWidth: 0,
                                }}
                              >
                                <span
                                  style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    color: C.accent,
                                  }}
                                >
                                  Dia {d.numeroDia}
                                  {diaData ? ` — ${fmtDateDMY(diaData)} (${diaSemana})` : ""}
                                </span>
                                {d.titulo && (
                                  <span
                                    style={{
                                      fontSize: 12,
                                      color: C.text,
                                      fontWeight: 500,
                                    }}
                                  >
                                    {d.titulo}
                                  </span>
                                )}
                              </div>
                              <span
                                style={{
                                  fontSize: 10,
                                  color: C.textDim,
                                  fontStyle: "italic",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                clique para editar
                              </span>
                            </div>
                            {empty ? (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: C.textMuted,
                                  fontStyle: "italic",
                                }}
                              >
                                Clique para preencher
                              </div>
                            ) : (
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: "4px 20px",
                                }}
                              >
                                {d.atividades && (
                                  <div style={{ fontSize: 11, color: C.textMuted }}>
                                    🎯 {d.atividades}
                                  </div>
                                )}
                                {d.transporte && (
                                  <div style={{ fontSize: 11, color: C.textMuted }}>
                                    🚗 {d.transporte}
                                  </div>
                                )}
                                {d.hospedagem && (
                                  <div style={{ fontSize: 11, color: C.textMuted }}>
                                    🏨 {d.hospedagem}
                                  </div>
                                )}
                                {d.refeicoes && (
                                  <div style={{ fontSize: 11, color: C.textMuted }}>
                                    🍽️ {d.refeicoes}
                                  </div>
                                )}
                                {d.notas && (
                                  <div
                                    style={{
                                      fontSize: 11,
                                      color: C.textMuted,
                                      gridColumn: "1 / -1",
                                    }}
                                  >
                                    📝 {d.notas}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>

                    {/* Seção de tarefas operacionais — checklist do roteiro */}
                    <div onClick={(e) => e.stopPropagation()}>
                      <RoteiroTarefasSection roteiro={r} onReload={reload} />
                    </div>
                  </div>
                  );
                })()}
              </div>
            );
          })}
          {roteiros.length === 0 && (
            <div style={{ fontSize: 13, color: C.textMuted, padding: 20, textAlign: "center" }}>
              Nenhum roteiro cadastrado ainda.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
